import React, { ReactNode } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';

export interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: 'error' | 'primary' | 'secondary' | 'success' | 'warning';
  children?: ReactNode; // For additional content like cards showing details
  loading?: boolean;
  /** Block the primary action (e.g. while a required follow-up field is empty). */
  confirmDisabled?: boolean;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmColor = 'error',
  children,
  loading = false,
  confirmDisabled = false,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {message}
          </Typography>
          {children}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={confirmColor}
          disabled={loading || confirmDisabled}
        >
          {loading ? 'Processing...' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** What a confirmed action will touch, one line each ("Class 9 · Section A" — 3),
 *  shown as `children` of ConfirmationDialog. */
export const ConfirmFacts: React.FC<{ items: Array<{ label: ReactNode; value: ReactNode }> }> = ({ items }) => (
  <Box sx={{ mt: 1.5, border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
    {items.map((it, i) => (
      <Box
        key={i}
        sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, px: 1.75, py: 1, '&:not(:last-of-type)': { borderBottom: 1, borderColor: 'divider' } }}
      >
        <Typography variant="body2" color="text.secondary">{it.label}</Typography>
        <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{it.value}</Typography>
      </Box>
    ))}
  </Box>
);

export default ConfirmationDialog;
